import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import type { ProviderLicenseType } from '@store/generated/organizations';
import {
  PROVIDER_LICENSE_TYPE_OPTIONS,
  type ProviderLicenseTypeOption,
} from '@utils/providerLicenseTypes';

import type {
  GeneratedIntakeField,
  GeneratedIntakeFormRequest,
  GeneratedIntakeGroupHelp,
  IntakeFieldValue,
  IntakeFormConfiguration,
  IntakeHubProviderRosterEntry,
} from '../../types';

import { GroupHelpTrigger, HelpPanelDrawer } from './HelpPanelDrawer';
import { IntakePageFieldInput } from './IntakePageFieldInput';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { IntakePayerTable, type IntakePayerTableProps } from './IntakePayerTable';
import { cardSx, chipSx, greyCardSx, groupTitleSx, wf } from './tokens';
import type { PayerRow } from './types';
import { useGroupHelpPanel } from './useGroupHelpPanel';

type ProviderRosterProgress = NonNullable<IntakeHubProviderRosterEntry['progress']>;

export interface EligibleProvider {
  userId: string;
  name: string;
  percentComplete?: ProviderRosterProgress['percent_complete'];
  status?: NonNullable<IntakeHubProviderRosterEntry['status']>;
  /** ISO timestamp when an admin verified this provider's intake, if any. */
  verifiedAt?: string | null;
}

export interface IntakeConfig {
  role: GeneratedIntakeFormRequest['role'];
  products: GeneratedIntakeFormRequest['products'];
  isPrescriber: boolean;
  licenseTypes: ProviderLicenseType[];
  practiceType: NonNullable<GeneratedIntakeFormRequest['practice_type']>;
  completedPages: NonNullable<IntakeFormConfiguration['completed_pages']>;
}

interface IntakeConfigPageProps {
  config: IntakeConfig;
  onChange: (next: Partial<IntakeConfig>) => void;
  payerRows: PayerRow[];
  payerTableProps?: Omit<IntakePayerTableProps, 'derivedSentence' | 'rows'>;
  eligibleProviders?: EligibleProvider[];
  eligibleProvidersLoading?: boolean;
  eligibleProvidersError?: boolean;
  rosterCandidates?: EligibleProvider[];
  canManageRoster?: boolean;
  onAddRosterProvider?: (userId: string) => void;
  onRemoveRosterProvider?: (userId: string) => void;
  isAddingRosterProvider?: boolean;
  derivedSentence: string | null;
  onContinue: () => void;
  groups?: GeneratedIntakeGroupHelp[];
  onHelpAction?: (action: 'message_specialist') => void;
  organizationScope?: boolean;
  readOnlyReason?: string;
  continueLabel?: string | null;
  saveLabel?: string;
  onSave?: () => void;
  saveDisabled?: boolean;
  saveErrorMessage?: string;
  availityField?: GeneratedIntakeField;
  draftValues?: Record<string, IntakeFieldValue>;
  acknowledgedValues?: Record<string, IntakeFieldValue>;
  onDraftChange?: (field: GeneratedIntakeField, value: IntakeFieldValue) => void;
  onPersistField?: (field: GeneratedIntakeField, value: IntakeFieldValue) => Promise<void>;
  disabledReasons?: Record<string, string>;
  saveMode?: 'autosave' | 'explicit';
  isSaving?: boolean;
  saveErrorCount?: number;
}

const ROLES: { value: IntakeConfig['role']; label: string }[] = [
  { value: 'clinic_manager', label: 'Clinic manager' },
  { value: 'provider', label: 'Provider' },
  { value: 'manager_who_also_practices', label: 'Manager who also sees patients' },
];

const PRODUCT_LABELS: Record<IntakeConfig['products'][number], string> = {
  jotbill: 'JotBill (billing)',
  jotcred: 'JotCred (credentialing)',
};

function providerIntakeStatusLabel(status: EligibleProvider['status']): string {
  if (status === 'complete') return 'Complete';
  if (status === 'in_progress') return 'In progress';
  if (status === 'invited') return 'Invited';
  return 'Unavailable';
}

function Radio({
  selected,
  label,
  onClick,
  disabled = false,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={event => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        padding: '4px 0',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.65 : 1,
      }}
    >
      <Box
        component="span"
        sx={{
          width: '13px',
          height: '13px',
          borderRadius: '50%',
          border: `1.5px solid ${selected ? wf.blue : wf.faint}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {selected && (
          <Box
            component="span"
            sx={{ width: '7px', height: '7px', borderRadius: '50%', background: wf.blue }}
          />
        )}
      </Box>
      {label}
    </Box>
  );
}

function GroupCard({
  title,
  chip,
  help,
  children,
  grey,
  full,
}: {
  title: string;
  chip?: string;
  help?: React.ReactNode;
  children: React.ReactNode;
  grey?: boolean;
  full?: boolean;
}) {
  return (
    <Box sx={{ ...(grey ? greyCardSx : cardSx), ...(full ? { gridColumn: '1 / -1' } : {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <Box sx={groupTitleSx}>{title}</Box>
        {help}
        {chip && <Box sx={chipSx('mut')}>{chip}</Box>}
      </Box>
      {children}
    </Box>
  );
}

export function IntakeConfigPage({
  config,
  onChange,
  payerRows,
  payerTableProps,
  eligibleProviders = [],
  eligibleProvidersLoading = false,
  eligibleProvidersError = false,
  rosterCandidates = [],
  canManageRoster = false,
  onAddRosterProvider,
  onRemoveRosterProvider,
  isAddingRosterProvider = false,
  derivedSentence,
  onContinue,
  groups = [],
  onHelpAction = () => undefined,
  organizationScope = false,
  readOnlyReason,
  continueLabel = "See what we'll need →",
  saveLabel,
  onSave,
  saveDisabled,
  saveErrorMessage,
  availityField,
  draftValues = {},
  acknowledgedValues = {},
  onDraftChange,
  onPersistField,
  disabledReasons = {},
  saveMode = 'autosave',
  isSaving = false,
  saveErrorCount = 0,
}: IntakeConfigPageProps) {
  const { selectedGroup, selectedGroupId, openGroup, closeGroup } = useGroupHelpPanel(groups);
  const drawerId = 'intake-config-group-help';
  const helpFor = (groupId: string, fallbackName: string) => {
    const group = groups.find(entry => entry.group_id === groupId);
    if (!group?.help_panel) return null;
    return (
      <GroupHelpTrigger
        groupName={group.group_name || fallbackName}
        controlsId={drawerId}
        expanded={selectedGroupId === groupId}
        onOpen={opener => openGroup(groupId, opener)}
      />
    );
  };
  const showsProviderRosterHandoff =
    organizationScope || (config.practiceType === 'group' && config.role !== 'provider');
  const [continueAttempted, setContinueAttempted] = useState(false);
  const availityValue = availityField ? draftValues[availityField.field_id] : undefined;
  const availityValid = !availityField?.is_required || availityValue === true;
  const availityDirty = Boolean(
    availityField &&
    (availityValue ?? false) !== (acknowledgedValues[availityField.field_id] ?? false),
  );
  const showAvailityError = !availityValid && (availityDirty || continueAttempted);
  const handleContinue = () => {
    setContinueAttempted(true);
    onContinue();
  };

  return (
    <>
      <IntakePageHeader
        title={organizationScope ? 'Organization intake settings' : 'A few questions first'}
        sub={
          organizationScope
            ? 'Review the intake provider roster and manage the organization payer list. Select a provider for provider-specific intake.'
            : 'Your answers decide exactly what we ask for — nothing else is shown.'
        }
      />

      {readOnlyReason && <Alert severity="info">{readOnlyReason}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
        {!organizationScope && (
          <>
            <GroupCard title="Your role" help={helpFor('C01', 'Your role')}>
              {ROLES.map(role => (
                <Radio
                  key={role.value}
                  selected={config.role === role.value}
                  label={role.label}
                  onClick={() => onChange({ role: role.value })}
                  disabled={Boolean(readOnlyReason)}
                />
              ))}
            </GroupCard>

            <GroupCard
              title="What we're setting up"
              chip="set by your JotPsych team"
              help={helpFor('C02', "What we're setting up")}
            >
              {config.products.length > 0 ? (
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {config.products.map(product => (
                    <Box
                      component="li"
                      key={product}
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '4px' }}
                    >
                      <Box
                        component="span"
                        aria-hidden="true"
                        sx={{ color: wf.green, fontSize: '14px', lineHeight: 1 }}
                      >
                        ✓
                      </Box>
                      <Typography sx={{ fontSize: '13px' }}>{PRODUCT_LABELS[product]}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: '13px', color: wf.muted }}>
                  No services have been assigned to this intake yet. Contact your JotPsych team.
                </Typography>
              )}
            </GroupCard>

            <GroupCard
              title="Do you prescribe medication?"
              help={helpFor('C03', 'Do you prescribe medication?')}
            >
              <Radio
                selected={config.isPrescriber}
                label="Yes"
                onClick={() => onChange({ isPrescriber: true })}
                disabled={Boolean(readOnlyReason)}
              />
              <Radio
                selected={!config.isPrescriber}
                label="No"
                onClick={() => onChange({ isPrescriber: false })}
                disabled={Boolean(readOnlyReason)}
              />
            </GroupCard>

            <GroupCard
              title="Your license / credential type(s)"
              help={helpFor('C04', 'Your license / credential type(s)')}
            >
              <Autocomplete<ProviderLicenseTypeOption, true>
                disabled={Boolean(readOnlyReason)}
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                options={PROVIDER_LICENSE_TYPE_OPTIONS}
                value={PROVIDER_LICENSE_TYPE_OPTIONS.filter(option =>
                  config.licenseTypes.includes(option.value),
                )}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_event, options) =>
                  onChange({ licenseTypes: options.map(option => option.value) })
                }
                renderValue={(selected, getItemProps) =>
                  selected.map((option, index) => {
                    const { key, ...chipProps } = getItemProps({ index });
                    return <Chip key={key} label={option.value} size="small" {...chipProps} />;
                  })
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select credentials"
                    placeholder={
                      config.licenseTypes.length === 0 ? 'Search credentials' : undefined
                    }
                    size="small"
                  />
                )}
              />
            </GroupCard>

            <GroupCard
              title="Solo or group practice"
              help={helpFor('C05', 'Solo or group practice')}
            >
              <Radio
                selected={config.practiceType === 'solo'}
                label="Solo"
                onClick={() => onChange({ practiceType: 'solo' })}
                disabled={Boolean(readOnlyReason)}
              />
              <Radio
                selected={config.practiceType === 'group'}
                label="Group"
                onClick={() => onChange({ practiceType: 'group' })}
                disabled={Boolean(readOnlyReason)}
              />
            </GroupCard>
          </>
        )}

        {showsProviderRosterHandoff && (
          <GroupCard
            title="Eligible providers"
            chip="managed by your practice"
            help={helpFor('R01', 'Eligible providers')}
            grey
          >
            {eligibleProvidersLoading ? (
              <Typography role="status" sx={{ fontSize: '13px', color: wf.muted, margin: 0 }}>
                Loading providers…
              </Typography>
            ) : eligibleProvidersError ? (
              <Typography role="alert" sx={{ fontSize: '13px', color: wf.muted, margin: 0 }}>
                We couldn&apos;t load this practice&apos;s providers. Refresh and try again.
              </Typography>
            ) : (
              <>
                {eligibleProviders.length === 0 ? (
                  <Typography sx={{ fontSize: '13px', color: wf.muted, margin: 0 }}>
                    No providers added yet. Add the providers who need credentialing or enrollment —
                    practice members who are only trialing SmartScribe are not included
                    automatically.
                  </Typography>
                ) : (
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                    {eligibleProviders.map(provider => {
                      const hasProgress =
                        provider.percentComplete !== undefined && provider.status !== undefined;

                      return (
                        <Box
                          component="li"
                          key={provider.userId}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'minmax(140px, 1fr) minmax(130px, 1fr) auto auto',
                            },
                            alignItems: 'center',
                            gap: { xs: '6px', sm: '12px' },
                            py: '8px',
                            borderTop: `1px solid ${wf.lineSoft}`,
                          }}
                        >
                          <Typography sx={{ color: wf.ink, fontSize: '13px', fontWeight: 650 }}>
                            {provider.name}
                          </Typography>
                          <Box>
                            {hasProgress ? (
                              <>
                                <LinearProgress
                                  aria-label={`${provider.name} intake progress`}
                                  variant="determinate"
                                  value={provider.percentComplete}
                                  sx={{
                                    height: 6,
                                    borderRadius: 4,
                                    backgroundColor: wf.track,
                                    '& .MuiLinearProgress-bar': { backgroundColor: wf.blue },
                                  }}
                                />
                                <Typography sx={{ color: wf.muted, fontSize: '12px', mt: '2px' }}>
                                  {provider.percentComplete}%
                                </Typography>
                              </>
                            ) : (
                              <Typography sx={{ color: wf.muted, fontSize: '12px' }}>
                                Progress unavailable
                              </Typography>
                            )}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              justifySelf: { xs: 'start', sm: 'end' },
                              borderRadius: '12px',
                              border: `1px solid ${provider.status === 'complete' ? 'transparent' : wf.line}`,
                              backgroundColor:
                                provider.status === 'complete' ? wf.greenSoft : wf.surfaceAlt,
                              color: provider.status === 'complete' ? wf.green : wf.muted,
                              fontSize: '11px',
                              fontWeight: 650,
                              px: '10px',
                              py: '3px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {providerIntakeStatusLabel(provider.status)}
                          </Box>
                          {canManageRoster && onRemoveRosterProvider && !readOnlyReason && (
                            <Button
                              aria-label={`Remove ${provider.name}`}
                              size="small"
                              disabled={isAddingRosterProvider}
                              onClick={() => onRemoveRosterProvider(provider.userId)}
                              sx={{ fontSize: '12px', minWidth: 0, color: wf.muted }}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {canManageRoster && onAddRosterProvider && !readOnlyReason && (
                  <Autocomplete
                    options={rosterCandidates}
                    getOptionLabel={option => option.name}
                    value={null}
                    blurOnSelect
                    disabled={isAddingRosterProvider}
                    loading={isAddingRosterProvider}
                    loadingText="Adding provider…"
                    onChange={(_event, option) => {
                      if (option) onAddRosterProvider(option.userId);
                    }}
                    noOptionsText="No more practice members to add"
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder={
                          isAddingRosterProvider ? 'Adding provider…' : 'Add a provider…'
                        }
                        slotProps={{
                          ...params.slotProps,
                          htmlInput: {
                            ...params.slotProps.htmlInput,
                            'aria-label': 'Add a provider to the intake roster',
                          },
                          input: {
                            ...params.slotProps.input,
                            endAdornment: (
                              <>
                                {isAddingRosterProvider && <CircularProgress size={16} />}
                                {params.slotProps.input.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                    sx={{ mt: '8px', maxWidth: '320px' }}
                  />
                )}
              </>
            )}
          </GroupCard>
        )}

        <IntakePayerTable
          {...payerTableProps}
          rows={payerRows}
          derivedSentence={derivedSentence}
          help={helpFor('C07', 'Your payer list')}
        />

        {availityField && (
          <GroupCard title="Availity access" help={helpFor('I23', 'Availity access')} full>
            <Box sx={{ color: wf.muted, fontSize: '12.5px', mb: 0.5 }}>
              {availityField.help_text}
            </Box>
            <IntakePageFieldInput
              field={{ ...availityField, help_text: '' }}
              draftValue={availityValue ?? false}
              acknowledgedValue={acknowledgedValues[availityField.field_id] ?? false}
              onDraftChange={onDraftChange ?? (() => undefined)}
              onPersist={onPersistField ?? (() => Promise.resolve())}
              disabled={Boolean(disabledReasons[availityField.field_id]) || Boolean(readOnlyReason)}
              disabledReason={disabledReasons[availityField.field_id]}
              saveMode={saveMode}
              showStatus={false}
              validationError={
                showAvailityError ? `${availityField.label} is required.` : undefined
              }
            />
          </GroupCard>
        )}
      </Box>

      {saveErrorCount > 0 && (
        <Box sx={{ color: 'error.main', fontSize: '0.8rem' }}>
          {saveErrorCount} change{saveErrorCount === 1 ? '' : 's'} could not be saved.
        </Box>
      )}

      {saveErrorMessage && <Alert severity="error">{saveErrorMessage}</Alert>}

      <IntakePageFooter
        primaryLabel={continueLabel ?? undefined}
        onPrimary={handleContinue}
        disabled={config.products.length === 0}
        saveLabel={saveLabel}
        onSave={onSave}
        saveDisabled={saveDisabled || (saveMode === 'explicit' && isSaving)}
      />
      <HelpPanelDrawer
        drawerId={drawerId}
        panel={selectedGroup?.help_panel ?? null}
        open={Boolean(selectedGroup?.help_panel)}
        onClose={closeGroup}
        onAction={onHelpAction}
      />
    </>
  );
}
